// src/features/services/stock/components/BalanceGrid.tsx
// ─────────────────────────────────────────────────────────────────────────────
// P3.1 — Stock Balance Grid.
// Renders a searchable table of all stock balances with availability info.
// ─────────────────────────────────────────────────────────────────────────────
import React, { useMemo, useState } from 'react';
import type { StockBalance } from '@/features/services/sales/api/stockApi';

interface BalanceGridProps {
  items: StockBalance[];
  isLoading: boolean;
  onSelectSku: (sku: string) => void;
}

export default function BalanceGrid({ items, isLoading, onSelectSku }: BalanceGridProps) {
  const [filter, setFilter] = useState('');

  const filtered = useMemo(() => {
    if (!filter) return items;
    const q = filter.toLowerCase();
    return items.filter(b => b.sku.toLowerCase().includes(q));
  }, [items, filter]);

  if (isLoading) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="pac-glass rounded-lg h-12 animate-pulse" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Search */}
      <input
        type="text"
        value={filter}
        onChange={e => setFilter(e.target.value)}
        placeholder="Filter by SKU…"
        className="pac-glass-input w-full max-w-xs rounded-md px-3 py-1.5 text-sm text-white placeholder-gray-500 border border-white/10 focus:border-emerald-500/50 focus:outline-none"
      />

      {/* Table */}
      <div className="pac-glass-raised rounded-lg overflow-hidden">
        <table className="w-full text-sm text-left">
          <thead>
            <tr className="border-b border-white/10 text-gray-400 text-xs uppercase tracking-wide">
              <th className="px-4 py-2">SKU</th>
              <th className="px-4 py-2 text-right">On Hand</th>
              <th className="px-4 py-2">Unit</th>
              <th className="px-4 py-2">Last Movement</th>
              <th className="px-4 py-2">Last Updated</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-4 py-6 text-center text-gray-500">
                  {items.length === 0 ? 'No stock balances.' : 'No matches.'}
                </td>
              </tr>
            ) : (
              filtered.map(b => (
                <tr
                  key={b.balance_key}
                  onClick={() => onSelectSku(b.sku)}
                  className="border-b border-white/5 hover:bg-white/5 cursor-pointer transition-colors"
                >
                  <td className="px-4 py-2 font-medium text-white">{b.sku.toUpperCase()}</td>
                  <td className="px-4 py-2 text-right tabular-nums text-white">{b.quantity}</td>
                  <td className="px-4 py-2 text-gray-400">{b.unit}</td>
                  <td className="px-4 py-2 text-gray-400">{b.last_movement_type ?? '—'}</td>
                  <td className="px-4 py-2 text-gray-500 text-xs">
                    {b.last_movement_at ? new Date(b.last_movement_at).toLocaleString() : '—'}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
